import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import type {CollapsibleLeftPaneConfig, PaneWithAriaLabel} from '@github-ui/three-panes-layout'

import {RepoIssuesSidebar} from './RepoIssuesSidebar'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'

/**
 * Shared sidebar configuration for every page that renders `RepoIssuesSidebar`
 * inside a `ThreePanesLayout`.
 *
 * Flag matrix:
 * - `issue_pinned_views` OFF → no sidebar (returns {}). The existing pages
 *   render without a left pane.
 * - `issue_pinned_views` ON + `issues_react_collapsible_sidebar` OFF → render
 *   the sidebar in a fixed non-collapsible left pane (legacy behaviour).
 *   Feedback link is still shown.
 * - `issue_pinned_views` ON + `issues_react_collapsible_sidebar` ON → render
 *   the sidebar inside a `CollapsibleSidebar` with a persisted toggle so the
 *   collapse state is shared across the issues, milestones, labels and
 *   views pages.
 *
 * Hook (not a plain `get` function) because `issue_pinned_views` is read via
 * `useFeatureFlag`, which consumes the React payload's `enabled_features` and
 * therefore honors repo/org actors. Must be called from a React component.
 */
export function useRepoIssuesSidebarPanes(): {
  collapsibleLeftPane?: CollapsibleLeftPaneConfig
  leftPane?: PaneWithAriaLabel
} {
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  if (!useFeatureFlag('issue_pinned_views')) {
    return {}
  }

  if (isFeatureEnabled('issues_react_collapsible_sidebar')) {
    return {
      collapsibleLeftPane: {
        storageKey: SIDEBAR_STORAGE_KEY,
        expandedWidth: 'small',
        render: () => <RepoIssuesSidebar collapsible />,
        renderFooter: ({isExpanded, toggle}) => (
          <>
            <CollapsibleFeedbackLink isExpanded={isExpanded} />
            <CollapsibleSidebar.Toggle isExpanded={isExpanded} onToggle={toggle} />
          </>
        ),
      },
    }
  }

  return {
    leftPane: {
      element: <RepoIssuesSidebar />,
      ariaLabel: SIDEBAR_ARIA_LABEL,
    },
  }
}
